import { useCallback, useContext, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faCheck, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    FixDataHygieneAddressParams,
    fixDataHygieneAddress,
    ignoreDataHygieneAddress,
    saveForLaterDataHygieneAddress,
    undoDataHygieneAddress,
    undoSaveForLaterDataHygieneAddress,
} from 'api/contacts/data-hygiene';
import { DataHygieneAddressContext } from 'assets/js/pages/contacts/data-hygiene/DataHygieneAddressContext';
import * as IgnoreButton from 'assets/js/pages/contacts/data-hygiene/IgnoreButton';
import { SaveForLaterButton } from 'assets/js/pages/contacts/data-hygiene/SaveForLaterButton';
import { TableRowSuccessState } from 'assets/js/pages/contacts/data-hygiene/TableRowSuccessState';
import * as Form from 'components/form';
import { TableContext, TableRowContext } from 'components/table/TableContext';
import { ORDERED_COUNTRIES, ORDERED_COUNTRIES_WITHOUT_USA } from 'constants/countries';
import { ORDERED_STATES } from 'constants/states';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { DataHygieneAddress } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AddressUpdateFormProps {
    editable?: boolean;
}

const SAVE_FOR_LATER_SUCCESS_DURATION = 1000;

const AddressUpdateForm = ({ editable = true }: AddressUpdateFormProps) => {
    const data = useContext(DataHygieneAddressContext);
    const {
        deliverability_status,
        provider_data,
        saved_for_later_at,
        id: addressCheckId,
    } = data.address;
    const { accountId } = useCurrentAccount();
    const { table } = useContext(TableContext);
    const { row } = useContext(TableRowContext);
    const shouldFillDefaultValues = !['undeliverable', 'no_match', 'invalid'].includes(
        deliverability_status,
    );
    const [latestMutationError, setLatestMutationError] = useState<string | null>(null);

    const stateMatch = useMemo(() => {
        if (!shouldFillDefaultValues) return undefined;

        const stateUppercase = provider_data?.state?.toUpperCase();

        return ORDERED_STATES.find(x => {
            const currentStateNameUppercase = x.name.toUpperCase();

            if (stateUppercase) {
                return (
                    stateUppercase === x.abbreviation ||
                    stateUppercase === currentStateNameUppercase
                );
            }

            return false;
        });
    }, [shouldFillDefaultValues, provider_data?.state]);

    const countryMatch = useMemo(() => {
        if (!shouldFillDefaultValues) return undefined;
        return ORDERED_COUNTRIES.find(x => provider_data?.country?.toUpperCase() === x.abbr);
    }, [shouldFillDefaultValues, provider_data?.country]);

    const methods = Form.useGivebutterForm<FixDataHygieneAddressParams>({
        defaultValues: shouldFillDefaultValues
            ? {
                  address_1: provider_data.address_1 ?? '',
                  address_2: provider_data.address_2 ?? '',
                  city: provider_data.city ?? '',
                  state: stateMatch?.abbreviation ?? '',
                  country: countryMatch?.abbr ?? '',
                  zipcode: provider_data.zipcode ?? '',
              }
            : {
                  address_1: '',
                  address_2: '',
                  city: '',
                  state: '',
                  country: '',
                  zipcode: '',
              },
    });
    const { handleSubmit, watch, control, setValue, path } = methods;
    const country = watch('country');
    const isUSA = country === 'USA';

    const saveForLaterMutationKey = `address-${addressCheckId}-save-for-later`;

    const handleOnMutate = useCallback(() => {
        if (table.options.enableRowPending) {
            table.addRowPending(row.id);
        }

        setLatestMutationError(null);
    }, [table, row.id]);

    const handleOnError = useCallback((error: GivebutterAxiosError) => {
        setLatestMutationError(error?.response?.data.message ?? 'An unknown error occurred.');
    }, []);

    const handleOnSettled = useCallback(() => {
        if (table.options.enableRowPending) {
            table.removeRowPending(row.id);
        }
    }, [table, row.id]);

    const {
        mutate: saveForLater,
        isPending: saveForLaterIsPending,
        isError: saveForLaterIsError,
    } = useMutation({
        mutationKey: [saveForLaterMutationKey],
        mutationFn: () => {
            return saveForLaterDataHygieneAddress(accountId, addressCheckId);
        },
        onMutate: handleOnMutate,
        onError: handleOnError,
        onSettled: handleOnSettled,
        onSuccess: () => {
            // NOTE: small delay to show success button state (before row gets removed)
            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.dataHygieneAddresses(accountId),
                });
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
                });
            }, SAVE_FOR_LATER_SUCCESS_DURATION);
        },
    });

    const undoSaveForLaterMutationKey = `address-${addressCheckId}-undo-save-for-later`;

    const {
        mutate: undoSaveForLater,
        isPending: undoSaveForLaterIsPending,
        isError: undoSaveForLaterIsError,
    } = useMutation({
        mutationKey: [undoSaveForLaterMutationKey],
        mutationFn: () => {
            return undoSaveForLaterDataHygieneAddress(accountId, addressCheckId);
        },
        onMutate: handleOnMutate,
        onError: handleOnError,
        onSettled: handleOnSettled,
        onSuccess: () => {
            // NOTE: small delay to show success button state (before row gets removed)
            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.dataHygieneAddresses(accountId),
                });
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
                });
            }, SAVE_FOR_LATER_SUCCESS_DURATION);
        },
    });

    const {
        mutate,
        isPending: updateIsPending,
        isError,
    } = useMutation<DataHygieneAddress, GivebutterAxiosError, FixDataHygieneAddressParams>({
        mutationFn: values => {
            return fixDataHygieneAddress(accountId, addressCheckId, {
                ...values,
            });
        },
        onMutate: handleOnMutate,
        onError: handleOnError,
        onSettled: handleOnSettled,
        onSuccess: () => {
            if (table.options.enableRowReplacement) {
                table.addRowReplacement({
                    rowId: row.id,
                    maxHeight: 100,
                    content: (
                        <TableRowSuccessState
                            key={addressCheckId}
                            successMessage={`Successfully updated address`}
                            undoMutationFn={() => undoDataHygieneAddress(accountId, addressCheckId)}
                            invalidationKeys={[
                                accountQueryKeys.dataHygieneAddresses(accountId),
                                accountQueryKeys.dataHygieneMetrics(accountId),
                            ]}
                        >
                            <Button
                                variant="solid"
                                color="success"
                                size="sm"
                                className="pointer-events-none"
                            >
                                <FontAwesomeIcon icon={faCheck} />
                                Saved
                            </Button>
                        </TableRowSuccessState>
                    ),
                });
            }
        },
    });

    const {
        mutate: ignoreUpdate,
        isPending: ignoreIsPending,
        isError: ignoreIsError,
    } = useMutation({
        mutationFn: () => ignoreDataHygieneAddress(accountId, addressCheckId),
        onMutate: handleOnMutate,
        onError: handleOnError,
        onSettled: handleOnSettled,
        onSuccess: () => {
            if (table.options.enableRowReplacement) {
                table.addRowReplacement({
                    rowId: row.id,
                    maxHeight: 100,
                    content: (
                        <TableRowSuccessState
                            key={addressCheckId}
                            successMessage={`Successfully ignored`}
                            undoMutationFn={() => undoDataHygieneAddress(accountId, addressCheckId)}
                            invalidationKeys={[
                                accountQueryKeys.dataHygieneAddresses(accountId),
                                accountQueryKeys.dataHygieneMetrics(accountId),
                            ]}
                        />
                    ),
                });
            }
        },
    });

    const hasLatestMutationError =
        latestMutationError &&
        (isError || ignoreIsError || saveForLaterIsError || undoSaveForLaterIsError);

    const addressIsPending =
        updateIsPending || ignoreIsPending || saveForLaterIsPending || undoSaveForLaterIsPending;

    const zipLabel = isUSA ? 'ZIP code' : 'Postal code';

    return (
        <Form.Provider {...methods}>
            <form
                className="w-full"
                onSubmit={handleSubmit(values => {
                    mutate(values);
                })}
            >
                {editable && (
                    <div className="flex flex-col gap-2">
                        <Form.Container>
                            <Form.Label name={path(`address_1`)} className="sr-only">
                                Address <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                name={'address_1'}
                                rules={{ required: 'Address is required' }}
                                control={control}
                            >
                                <Form.TextInputForm placeholder={'Address'} />
                            </Form.TextInputRoot>
                            <Form.Error name={path('address_1')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path(`address_2`)} className="sr-only">
                                Address Line 2
                            </Form.Label>
                            <Form.TextInputRoot name={'address_2'} control={control}>
                                <Form.TextInputForm placeholder={'Apartment, suite, etc.'} />
                            </Form.TextInputRoot>
                            <Form.Error name={path('address_2')} />
                        </Form.Container>

                        <div className={'flex gap-2'}>
                            <Form.Container className="w-auto flex-grow">
                                <Form.Label name={path(`city`)} className="sr-only">
                                    City <Form.LabelAsterisk />
                                </Form.Label>
                                <Form.TextInputRoot
                                    name={'city'}
                                    control={control}
                                    rules={{ required: 'City is required' }}
                                >
                                    <Form.TextInputForm placeholder={'City'} />
                                </Form.TextInputRoot>
                                <Form.Error name={path('city')} />
                            </Form.Container>
                            <Form.Container className="w-auto">
                                <Form.Label name={path(`state`)} className="sr-only">
                                    {isUSA ? 'State' : 'Region'}
                                </Form.Label>
                                {isUSA ? (
                                    <Form.SingleSelect control={control} name="state">
                                        <SingleSelect.Trigger>
                                            <SingleSelect.Value placeholder="State" />
                                        </SingleSelect.Trigger>
                                        <SingleSelect.Content>
                                            {ORDERED_STATES.map(state => (
                                                <SingleSelect.Item
                                                    key={state.abbreviation}
                                                    value={state.abbreviation}
                                                >
                                                    <SingleSelect.ItemText>
                                                        {state.abbreviation.toUpperCase()}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                ) : (
                                    <Form.TextInputRoot name={'state'} control={control}>
                                        <Form.TextInputForm placeholder={'Region'} />
                                    </Form.TextInputRoot>
                                )}
                                <Form.Error name={path('state')} />
                            </Form.Container>
                            <Form.Container className="w-auto flex-grow">
                                <Form.Label name={path(`zipcode`)} className="sr-only">
                                    {zipLabel}
                                </Form.Label>
                                <Form.TextInputRoot name={'zipcode'} control={control}>
                                    <Form.TextInputForm placeholder={zipLabel} />
                                </Form.TextInputRoot>
                                <Form.Error name={path('zipcode')} />
                            </Form.Container>
                        </div>

                        <Form.Container>
                            <Form.Label name={path(`country`)} className="sr-only">
                                Country
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.SingleSelect
                                control={control}
                                name="country"
                                rules={{ required: 'Country is required' }}
                                onValueChange={value => {
                                    // NOTE: clear state if country changes to/from USA
                                    if ((isUSA && value !== 'USA') || (!isUSA && value === 'USA')) {
                                        setValue('state', '');
                                    }
                                }}
                            >
                                <SingleSelect.Trigger>
                                    <SingleSelect.Value placeholder="Country" />
                                </SingleSelect.Trigger>
                                <SingleSelect.Content>
                                    <SingleSelect.Item value={'USA'}>
                                        <SingleSelect.ItemText>United States</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Separator />
                                    {ORDERED_COUNTRIES_WITHOUT_USA.map(country => (
                                        <SingleSelect.Item key={country.name} value={country.abbr}>
                                            <SingleSelect.ItemText>
                                                {country.name}
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    ))}
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                            <Form.Error name={path('country')} />
                        </Form.Container>
                    </div>
                )}

                {hasLatestMutationError && (
                    <div className="text-sm font-medium text-red-500">{latestMutationError}</div>
                )}

                <div className="flex items-center justify-between gap-4 pt-2">
                    <SaveForLaterButton
                        disabled={updateIsPending || ignoreIsPending}
                        isSaved={saved_for_later_at !== null}
                        saveForLater={saveForLater}
                        saveForLaterMutationKey={saveForLaterMutationKey}
                        undoSaveForLater={undoSaveForLater}
                        undoSaveForLaterMutationKey={undoSaveForLaterMutationKey}
                    />

                    <div className="flex justify-end gap-2">
                        <IgnoreButton.Root>
                            <IgnoreButton.Button
                                disabled={addressIsPending}
                                onClick={() => ignoreUpdate()}
                            >
                                {ignoreIsPending ? (
                                    <>
                                        <FontAwesomeIcon
                                            className="animate-spin"
                                            icon={faSpinnerThird}
                                        />
                                        Ignoring
                                    </>
                                ) : (
                                    'Ignore'
                                )}
                            </IgnoreButton.Button>
                            <IgnoreButton.AlertContent>
                                <IgnoreButton.AlertBody>
                                    <IgnoreButton.Title />
                                    <IgnoreButton.Description />
                                </IgnoreButton.AlertBody>
                                <IgnoreButton.AlertAction onClick={() => ignoreUpdate()} />
                            </IgnoreButton.AlertContent>
                        </IgnoreButton.Root>

                        <Form.SubmitButton
                            disabled={addressIsPending}
                            className="!h-9 !px-3 !py-2 !text-sm"
                        >
                            {updateIsPending ? 'Updating...' : 'Update'}
                        </Form.SubmitButton>
                    </div>
                </div>
            </form>
        </Form.Provider>
    );
};

export { AddressUpdateForm };
